import { useState, useRef, useCallback, useEffect } from 'react';
import { HubConnection, HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import { api } from '../services/api';
import { Account } from '../types';

export interface TranscriptSegment {
  id: string;
  text: string;
  isPartial: boolean;
  timestamp: Date;
  channelId: string;
  participantRole: string;
}

export interface HealthScribeSession {
  sessionId: string;
  patientId: string;
  noteTemplate: string;
  status: 'idle' | 'connecting' | 'streaming' | 'stopping' | 'completed' | 'error';
  transcriptSegments: TranscriptSegment[];
  error?: string;
  userRequestId?: string; // Track the UserRequest ID for this session
}

// SignalR event data types
interface StreamingStartedData {
  sessionId: string;
  status: string;
}

interface TranscriptReceivedData {
  sessionId: string;
  segment: {
    id: string;
    text: string;
    isPartial: boolean;
    timestamp: string;
    channelId: string;
    participantRole: string;
  };
}

interface FinalTranscriptReceivedData {
  sessionId: string;
  transcript: string;
}

interface StreamingStatusData {
  sessionId: string;
  status: string;
}

interface StreamingErrorData {
  sessionId: string;
  error: string;
}

export const useHealthScribeStreaming = () => {
  const [session, setSession] = useState<HealthScribeSession | null>(null);
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [connectionState, setConnectionState] = useState<'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'failed'>('disconnected');
  const [connectionError, setConnectionError] = useState<string>('');
  const [retryCount, setRetryCount] = useState(0);
  const [maxRetries] = useState(3);
  
  // Refs to avoid stale closure bugs in audio callbacks
  const isRecordingRef = useRef(false);
  const isPausedRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);
  const userRequestIdRef = useRef<string | null>(null);
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  // Audio pipeline monitoring - track chunk count for diagnostics
  const audioChunkCountRef = useRef<number>(0);

  // Helper to detect permanent errors that shouldn't be retried
  const isPermanentError = (error: Error | undefined): boolean => {
    if (!error) return false;
    const message = error.message.toLowerCase();
    
    // CORS errors, auth errors, and negotiation failures are permanent
    return message.includes('cors') ||
           message.includes('negotiate') ||
           message.includes('access-control-allow-origin') ||
           message.includes('unauthorized') ||
           message.includes('forbidden') ||
           message.includes('failed to complete negotiation');
  };

  // Initialize SignalR connection
  const initializeConnection = useCallback(async () => {
    try {
      setConnectionState('connecting');
      setConnectionError('');
      
      // On iOS, Capacitor's HTTP plugin intercepts negotiation and breaks it
      // Skip negotiation and connect directly via WebSockets
      const isIOS = (window as any).Capacitor?.getPlatform() === 'ios';
      
      const hubConnection = new HubConnectionBuilder()
        .withUrl(`${import.meta.env.VITE_API_URL}/healthscribehub`, {
          skipNegotiation: isIOS,
          transport: HttpTransportType.WebSockets
        })
        .withHubProtocol(new MessagePackHubProtocol())
        .withAutomaticReconnect([0, 2000, 5000, 10000])
        .configureLogging(LogLevel.Information)
        .build();

      // Enhanced connection state handlers - no manual retry, let SignalR handle it
      hubConnection.onclose(err => {
        console.log('🔌 SignalR connection closed:', err);
        setConnectionState('disconnected');
        setIsConnected(false);
        
        // Check if this is a permanent error
        if (err && isPermanentError(err)) {
          console.error('❌ Permanent connection error detected, stopping retries:', err.message);
          setConnectionState('failed');
          setConnectionError(`Connection failed: ${err.message}. Please check your network and server configuration.`);
          // Don't retry - this is a permanent error
          return;
        }
        
        if (err) {
          setConnectionError(err.message || 'Connection lost');
        }
        
        // SignalR's .withAutomaticReconnect() will handle retries for transient errors
        // No need for manual retry logic here
      });
      
      hubConnection.onreconnecting(err => {
        console.log('🔄 SignalR reconnecting:', err);
        
        // Check if this is a permanent error before attempting reconnect
        if (err && isPermanentError(err)) {
          console.error('❌ Permanent error during reconnection, stopping:', err.message);
          setConnectionState('failed');
          setConnectionError(`Connection failed: ${err.message}. Please check your network and server configuration.`);
          hubConnection.stop(); // Stop the connection to prevent further retries
          return;
        }
        
        setConnectionState('reconnecting');
        setIsConnected(false);
        if (err) {
          setConnectionError(`Reconnecting... (${err.message})`);
        } else {
          setConnectionError('Reconnecting...');
        }
      });
      
      hubConnection.onreconnected(id => {
        console.log('✅ SignalR reconnected. ConnId:', id);
        setConnectionState('connected');
        setIsConnected(true);
        setConnectionError('');
        setRetryCount(0); // Reset retry count on successful reconnection
      });

      // Set up event handlers with proper typing
      hubConnection.on('StreamingStarted', (data: StreamingStartedData) => {
        console.log('Streaming started:', data);
        setSession(prev => prev ? { ...prev, status: 'streaming' } : null);
      });

      hubConnection.on('TranscriptReceived', (data: TranscriptReceivedData) => {
        console.log('Transcript received:', data);
        setSession(prev => {
          if (!prev) return null;
          return {
            ...prev,
            transcriptSegments: [...prev.transcriptSegments, {
              id: data.segment.id,
              text: data.segment.text,
              isPartial: data.segment.isPartial,
              timestamp: new Date(data.segment.timestamp),
              channelId: data.segment.channelId,
              participantRole: data.segment.participantRole
            }]
          };
        });
      });

      hubConnection.on('FinalTranscriptReceived', (data: FinalTranscriptReceivedData) => {
        console.log('Final transcript received:', data);
        setSession(prev => {
          if (!prev) return null;
          return {
            ...prev,
            transcriptSegments: [...prev.transcriptSegments, {
              id: crypto.randomUUID(),
              text: data.transcript,
              isPartial: false,
              timestamp: new Date(),
              channelId: "0",
              participantRole: "CLINICIAN"
            }]
          };
        });
      });

      hubConnection.on('StreamingStopped', (data: StreamingStatusData) => {
        console.log('Streaming stopped:', data);
        setSession(prev => prev ? { ...prev, status: 'completed' } : null);
        setIsRecording(false);
        setIsPaused(false);
        isPausedRef.current = false;
      });

      hubConnection.on('StreamingCompleted', (data: StreamingStatusData) => {
        console.log('Streaming completed:', data);
        setSession(prev => prev ? { ...prev, status: 'completed' } : null);
        setIsRecording(false);
        setIsPaused(false);
        isPausedRef.current = false;
      });

      hubConnection.on('StreamingError', (data: StreamingErrorData) => {
        console.error('Streaming error:', data);
        setSession(prev => prev ? { ...prev, status: 'error', error: data.error } : null);
        setIsRecording(false);
        setIsPaused(false);
        isPausedRef.current = false;
        isRecordingRef.current = false; // Stop audio processing immediately
        setConnectionError(data.error); // Show error to user
      });

      hubConnection.on('StreamingPaused', (data: StreamingStatusData) => {
        console.log('Streaming paused:', data);
        setIsPaused(true);
        isPausedRef.current = true;
      });

      hubConnection.on('StreamingResumed', (data: StreamingStatusData) => {
        console.log('Streaming resumed:', data);
        setIsPaused(false);
        isPausedRef.current = false;
      });

      // Start connection
      await hubConnection.start();
      setConnection(hubConnection);
      setIsConnected(true);
      setConnectionState('connected');
      setConnectionError('');
      setRetryCount(0); // Reset retry count on successful connection
      
      console.log('✅ Connected to HealthScribe hub');
    } catch (error) {
      console.error('❌ Failed to connect to HealthScribe hub:', error);
      
      // Check if this is a permanent error
      const err = error instanceof Error ? error : new Error('Failed to connect');
      if (isPermanentError(err)) {
        console.error('❌ Permanent connection error, will not retry:', err.message);
        setConnectionState('failed');
        setConnectionError('Connection has been lost');
      } else {
        setConnectionState('failed');
        setConnectionError('Connection has been lost');
      }
      
      setIsConnected(false);
      throw error;
    }
  }, []);

  // Start a new streaming session with UserRequest creation
  const startSession = useCallback(async (patientId: string, noteTemplate: string = 'PHYSICAL_SOAP', account?: Account, taskDefinitionId?: string) => {
    if (!connection) {
      throw new Error('Not connected');
    }

    try {
      // FIRST: Create UserRequest if account and taskDefinitionId are provided
      // This will give us the UserRequestId which we'll use as the sessionId
      let userRequestId: string | undefined;
      if (account && taskDefinitionId) {
        try {
          // Create UserRequest first - we'll use its ID as the sessionId
          const userRequest = await api.startStreaming(patientId, noteTemplate, taskDefinitionId, account);
          userRequestId = userRequest.id;
          userRequestIdRef.current = userRequestId;
          
          console.log('✅ Created UserRequest for streaming session:', userRequestId);
        } catch (error) {
          console.error('⚠️ Failed to create UserRequest, continuing with streaming only:', error);
          // Don't fail the entire session if UserRequest creation fails
        }
      }
      
      // Use UserRequestId as sessionId if available, otherwise generate a random one
      const sessionId = userRequestId || crypto.randomUUID();
      
      // Detect actual sample rate by requesting microphone access
      console.log('🎤 Requesting microphone access to detect sample rate...');
      const tempStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      // Create temporary AudioContext to detect sample rate
      const tempAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const actualSampleRate = tempAudioContext.sampleRate;
      console.log('🎵 Detected actual sample rate:', actualSampleRate, 'Hz');
      
      // Clean up temporary resources
      tempStream.getTracks().forEach(track => track.stop());
      await tempAudioContext.close();
      
      const newSession: HealthScribeSession = {
        sessionId,
        patientId,
        noteTemplate,
        status: 'connecting',
        transcriptSegments: [],
        userRequestId
      };

      setSession(newSession);
      sessionIdRef.current = sessionId; // Keep ref in sync
      console.log('🎬 Created new session:', sessionId, 'with status:', newSession.status);

      // Start streaming on the hub with the correct sample rate from the beginning
      await connection.invoke('StartStreaming', sessionId, patientId, noteTemplate, actualSampleRate);
      
      console.log('✅ Started HealthScribe session:', sessionId, 'with sample rate:', actualSampleRate);
    } catch (error) {
      console.error('❌ Failed to start session:', error);
      setSession(prev => prev ? { ...prev, status: 'error', error: error instanceof Error ? error.message : 'Unknown error' } : null);
      throw error;
    }
  }, [connection]);


  // Start recording audio with AudioWorklet (modern replacement for ScriptProcessorNode)
  const startRecording = useCallback(async (deviceId?: string) => {
    if (!sessionIdRef.current || !connection) {
      throw new Error('No active session or connection');
    }

    try {
      // Request microphone access - let browser use optimal settings
      const audioConstraints: MediaTrackConstraints = {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      };
      
      // Add device ID if specified
      if (deviceId) {
        audioConstraints.deviceId = { exact: deviceId };
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: audioConstraints
      });

      streamRef.current = stream;

      // Create audio context - let browser use its preferred sample rate
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = audioContext;

      // Log actual sample rate for debugging (should match what we detected earlier)
      console.log('🎵 AudioContext sample rate during recording:', audioContext.sampleRate);

      // Load AudioWorklet processor
      // Use absolute URL for both web and Capacitor compatibility
      try {
        // Try to construct a proper URL that works for both web and native
        const baseUrl = window.location.origin;
        const audioProcessorUrl = `${baseUrl}/audio-processor.js`;
        console.log('🎵 Loading audio processor from:', audioProcessorUrl);
        await audioContext.audioWorklet.addModule(audioProcessorUrl);
        console.log('✅ Audio processor loaded successfully');
      } catch (error) {
        console.error('❌ Failed to load audio processor:', error);
        // Try fallback with relative path
        console.log('🔄 Trying fallback path: /audio-processor.js');
        await audioContext.audioWorklet.addModule('/audio-processor.js');
      }

      // Create source node from microphone stream
      const source = audioContext.createMediaStreamSource(stream);
      
      // Create AudioWorklet node (modern replacement for ScriptProcessorNode)
      const workletNode = new AudioWorkletNode(audioContext, 'audio-pcm-processor');
      workletNodeRef.current = workletNode;

      // Handle messages from the audio worklet
      workletNode.port.onmessage = (event) => {
        const sid = sessionIdRef.current;
        if (!isRecordingRef.current || !sid) {
          return;
        }

        // Check connection state before processing audio
        if (!connection || !isConnected) {
          console.warn('⚠️ Audio callback skipped - no active connection');
          return;
        }

        if (event.data.type === 'audio') {
          const bytes = event.data.data;
          
          // Track chunks for diagnostics
          audioChunkCountRef.current++;
          
          // Log every 100 chunks (~4 seconds) for monitoring
          if (audioChunkCountRef.current % 100 === 0) {
            console.log('🎤 Audio chunks sent:', audioChunkCountRef.current);
          }

          // If paused, drop audio at client to save bandwidth; Node injects silence
          if (isPausedRef.current) {
            return;
          }

          if (connection && sid) {
            connection.send('SendAudioChunk', sid, bytes).catch(err => {
              console.error('❌ SendAudioChunk failed:', err);
              setConnectionError('Connection lost. Recording stopped.');
            });
          }
        }
      };

      // Connect the audio nodes
      source.connect(workletNode);
      workletNode.connect(audioContext.destination);

      // Update session status to streaming
      setSession(prev => prev ? { ...prev, status: 'streaming' } : null);
      setIsRecording(true);
      isRecordingRef.current = true;
      setIsPaused(false);
      isPausedRef.current = false;
      audioChunkCountRef.current = 0; // Reset chunk counter
      console.log('✅ Started recording audio with AudioWorklet');
    } catch (error) {
      console.error('❌ Failed to start recording:', error);
      throw error;
    }
  }, [session, connection, isRecording]);

  // Stop recording audio
  const stopRecording = useCallback(async () => {
    try {
      // First, update the recording state to stop audio callbacks
      setIsRecording(false);
      isRecordingRef.current = false; // Keep ref in sync
      
      // Stop the media stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      // Clean up AudioWorklet node
      if (workletNodeRef.current) {
        workletNodeRef.current.port.onmessage = null; // Remove message handler
        workletNodeRef.current.disconnect();
        workletNodeRef.current = null;
      }

      if (audioContextRef.current) {
        try {
          // Check if AudioContext is already closed
          if (audioContextRef.current.state !== 'closed') {
            await audioContextRef.current.close();
          }
        } catch (error) {
          // Ignore errors if already closed
          console.warn('AudioContext close error (may already be closed):', error);
        }
        audioContextRef.current = null;
      }

      // Update session status back to connecting (ready for more recording)
      setSession(prev => prev ? { ...prev, status: 'connecting' } : null);
      
      // Stop the HealthScribe stream to send END_OF_SESSION
      if (session && connection) {
        console.log('🛑 Stopping HealthScribe stream for session:', session.sessionId);
        await connection.invoke('StopStreaming', session.sessionId);
      }
      
      console.log('✅ Stopped recording audio');
    } catch (error) {
      console.error('❌ Failed to stop recording:', error);
      throw error;
    }
  }, [session, connection]);

  // Pause streaming (keep mic pipeline intact, drop audio client-side)
  const pauseStreaming = useCallback(async () => {
    if (!sessionIdRef.current || !connection) return;
    try {
      await connection.invoke('PauseStreaming', sessionIdRef.current);
      setIsPaused(true);
      isPausedRef.current = true;
    } catch (e) {
      console.error('❌ Failed to pause streaming:', e);
      throw e;
    }
  }, [connection]);

  // Resume streaming
  const resumeStreaming = useCallback(async () => {
    if (!sessionIdRef.current || !connection) return;
    try {
      await connection.invoke('ResumeStreaming', sessionIdRef.current);
      setIsPaused(false);
      isPausedRef.current = false;
    } catch (e) {
      console.error('❌ Failed to resume streaming:', e);
      throw e;
    }
  }, [connection]);

  // Stop the streaming session and complete UserRequest
  const stopSession = useCallback(async () => {
    if (!sessionIdRef.current || !connection) {
      throw new Error('No active session or connection');
    }

    console.log('🛑 stopSession called for session:', sessionIdRef.current, 'current status:', session?.status);

    try {
      // Stop recording first
      if (isRecording) {
        console.log('🛑 Stopping recording before stopping session');
        await stopRecording();
      }

      // Stop streaming on the hub
      console.log('🛑 Invoking StopStreaming on hub for session:', sessionIdRef.current);
      await connection.invoke('StopStreaming', sessionIdRef.current);
      
      // Complete UserRequest if we have one
      if (userRequestIdRef.current && session) {
        try {
          const fullTranscript = session.transcriptSegments
            .map(segment => segment.text)
            .join(' ');
          
          console.log('🏁 Completing UserRequest - background service will handle analytics');
          
          await api.completeStreaming(
            userRequestIdRef.current,
            sessionIdRef.current,
            fullTranscript,
            undefined, // Backend will handle analytics polling
            'COMPLETED'
          );
          
          console.log('✅ UserRequest sent to backend for processing:', userRequestIdRef.current);
        } catch (error) {
          console.error('⚠️ Failed to complete UserRequest:', error);
          // Don't fail the session stop if UserRequest completion fails
        }
      }
      
      console.log('✅ Stopped HealthScribe session:', sessionIdRef.current);
      
      // Clear session refs
      sessionIdRef.current = null;
      userRequestIdRef.current = null;
    } catch (error) {
      console.error('❌ Failed to stop session:', error);
      throw error;
    }
  }, [session, connection, isRecording, stopRecording]);

  // Manual retry connection function
  const retryConnection = useCallback(async () => {
    try {
      setRetryCount(0); // Reset retry count for manual retry
      setConnectionError('');
      setConnectionState('connecting');
      await initializeConnection();
    } catch (error) {
      console.error('❌ Manual retry failed:', error);
      const err = error instanceof Error ? error : new Error('Retry failed');
      
      // Don't encourage retrying if it's a permanent error
      if (isPermanentError(err)) {
        setConnectionError(`${err.message} - This appears to be a configuration issue.`);
      } else {
        setConnectionError('Connection has been lost');
      }
    }
  }, [initializeConnection]);

  // Handle reconnection and session rebinding
  const handleReconnection = useCallback(async () => {
    if (!connection || !sessionIdRef.current) return;
    
    try {
      const sid = sessionIdRef.current;
      await connection.invoke('RebindSession', sid);
      console.log('🔗 Rebound session to new connection:', sid);
      
      // Verify the session is still active after rebinding
      const isActive = await connection.invoke('IsSessionActive', sid);
      console.log('🔍 Session active after rebind:', { sessionId: sid, isActive });
      
      if (!isActive) {
        console.warn('⚠️ Session not active after rebind, may need to restart');
        setConnectionError('Connection has been lost');
        // Optionally restart the session here if needed
      }
    } catch (e) {
      console.error('Failed to rebind session', e);
      setConnectionError('Connection has been lost');
    }
  }, [connection]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Unmount cleanup only. Use refs to avoid stale state.
      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
        if (workletNodeRef.current) {
          workletNodeRef.current.port.onmessage = null;
          workletNodeRef.current.disconnect();
          workletNodeRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }
      } finally {
        // Close the hub connection only when the component unmounts.
        if (connection) {
          connection.stop().catch(() => {});
        }
      }
    };
  }, []); // ← empty deps: only runs on unmount

  return {
    session,
    isConnected,
    isRecording,
    isPaused,
    connectionState,
    connectionError,
    retryCount,
    maxRetries,
    startSession,
    startRecording,
    stopRecording,
    stopSession,
    initializeConnection,
    retryConnection,
    handleReconnection,
    pauseStreaming,
    resumeStreaming
  };
};
